import React, { useMemo } from 'react'
import { Stack, Typography, useTheme, Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useKpis } from '../use-projects'

interface Props {
  value: number
  label: string
  onClick?: () => void
}
const Counter: React.FC<Props> = ({
  value,
  label,
  onClick = null
}) => {
  return (
    <Stack direction={'row'} gap={1.5} sx={{cursor: onClick ? 'pointer' : 'inherit'}} onClick={onClick ? onClick : () => {}}>
      <Typography component={'span'} sx={{fontSize: 24, color: '#5D5D5D'}}>{value}</Typography>
      <Typography component={'span'} sx={{fontSize: 12, color: '#5D5D5D'}}>{label}</Typography>
    </Stack>
  )
}
const Kpi = ({ icon, title, loading, children }) => {
  const theme = useTheme()
  return (
    <Stack
      flex={1}
      gap={2}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '24px',
        padding: 2
      }}>
      <Stack direction={'row'} alignItems={'center'} gap={1.5}>
        <i className={`fa-solid fa-${icon}`} style={{color: theme.palette.primary.main}} />
        <Typography component={'span'} sx={{fontSize: 12, fontWeight: 500, color: theme.palette.grey['800']}}>{title}</Typography>
      </Stack>
      <Stack sx={{height: 36}}>
      {
        loading
          ? <Skeleton variant={'rectangular'} sx={{width: 100, height: 36, borderRadius: 2}} />
          : children
      }
      </Stack>
    </Stack>
  )
}

export const KpisHome = ({canClick = false}:{canClick: boolean} ) => {
  const {t} = useTranslation()
  const { loading, data } = useKpis()
  const navigate = useNavigate()
  const projectInProgress = useMemo(() => {
    return data ? data.find(({key}) => key === 'projects_in_progress').count : 0
  }, [data])
  const serviceInProgress = useMemo(() => {
    return data ? data.find(({key}) => key === 'services_in_progress').count : 0
  }, [data])
  if (!loading && !serviceInProgress && !projectInProgress) {
    return null
  }
  return (
    <Stack direction={'row'} gap={2}>
      <Kpi icon={'clipboard-list-check'} title={t('projects.kpis.project')} loading={loading}>
        <Counter
          value={projectInProgress}
          label={t('projects.table.status.in_progress')}
          onClick={canClick ? () => navigate('/projects') : undefined}
        />
      </Kpi>
      <Kpi icon={'badge-check'} title={t('projects.kpis.services')} loading={loading}>
        <Counter
          value={serviceInProgress}
          label={t('projects.table.status.in_progress')}
          onClick={canClick ? () => navigate('/projects') : undefined}
        />
      </Kpi>
    </Stack>
  )
}

const Kpis = () => {
  const {t} = useTranslation()
  const { loading, data } = useKpis()
  const projectInProgress = useMemo(() => {
    return data ? data.find(({key}) => key === 'projects_in_progress').count : 0
  }, [data])
  const projectProduction = useMemo(() => {
    return data ? data.find(({key}) => key === 'projects_production').count : 0
  }, [data])
  const serviceInProgress = useMemo(() => {
    return data ? data.find(({key}) => key === 'services_in_progress').count : 0
  }, [data])
  const serviceProduction = useMemo(() => {
    return data ? data.find(({key}) => key === 'services_production').count : 0
  }, [data])
  return (
    <Stack direction={'row'} gap={2}>
      <Kpi icon={'clipboard-list-check'} title={t('projects.kpis.project')} loading={loading}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Counter value={projectInProgress} label={t('projects.table.status.in_progress')} />
          <Counter value={projectProduction} label={t('projects.table.status.production', {count: projectProduction})} />
        </Stack>
      </Kpi>
      <Kpi icon={'badge-check'} title={t('projects.kpis.services')} loading={loading}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Counter value={serviceInProgress} label={t('projects.table.status.in_progress')} />
          <Counter value={serviceProduction} label={t('projects.table.status.production', {count: serviceProduction})} />
        </Stack>
      </Kpi>
    </Stack>
  )
}

export default Kpis